import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  Trash2,
  Pencil,
  Search,
  Upload,
  ExternalLink,
  GripVertical,
  X,
  Check,
  ImagePlus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = () => ({
  id: null,
  company_name: "",
  website_url: "",
  project_count: "",
});

/**
 * Admin tab to manage Client Projects (logos, name, order, website, project count).
 */
const ClientsManager = ({ token, onChange }) => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [editForm, setEditForm] = useState(null);
  const [bulkText, setBulkText] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const headers = { "x-admin-token": token };
  const logoInputRefs = useRef({}); // id → input ref

  const reload = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients`, { headers });
      setClients(res.data || []);
    } catch (_e) {
      toast.error("Failed to load clients");
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const filtered = clients.filter((c) =>
    !search.trim()
      ? true
      : c.company_name.toLowerCase().includes(search.trim().toLowerCase())
  );

  const saveForm = async () => {
    const name = editForm.company_name.trim();
    if (!name) {
      toast.error("Company name is required");
      return;
    }
    const body = {
      company_name: name,
      website_url: editForm.website_url.trim(),
      project_count:
        editForm.project_count === "" ? null : Number(editForm.project_count) || null,
    };
    try {
      if (editForm.id) {
        await axios.put(`${API}/admin/clients/${editForm.id}`, body, { headers });
        toast.success("Client updated");
      } else {
        await axios.post(`${API}/admin/clients`, body, { headers });
        toast.success("Client added");
      }
      setEditForm(null);
      await reload();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const removeClient = async (c) => {
    if (!window.confirm(`Delete "${c.company_name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/clients/${c.id}`, { headers });
      toast.success("Client deleted");
      await reload();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const uploadLogo = async (c, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo too large (max 5 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/clients/${c.id}/logo`, fd, { headers });
      toast.success("Logo uploaded");
      await reload();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const submitBulk = async () => {
    const names = bulkText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!names.length) {
      toast.error("Paste at least one name per line");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/clients/bulk`, { names }, { headers });
      toast.success(`Added ${res.data.added} client${res.data.added === 1 ? "" : "s"}`);
      setBulkText("");
      setShowBulk(false);
      await reload();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk add failed");
    }
  };

  // Drag handlers
  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e, id) => {
    e.preventDefault();
    if (id !== dragOverId) setDragOverId(id);
  };
  const onDragEnd = async () => {
    if (!dragId || !dragOverId || dragId === dragOverId) {
      setDragId(null);
      setDragOverId(null);
      return;
    }
    const ordered = [...clients];
    const from = ordered.findIndex((c) => c.id === dragId);
    const to = ordered.findIndex((c) => c.id === dragOverId);
    if (from < 0 || to < 0) return;
    const [moved] = ordered.splice(from, 1);
    ordered.splice(to, 0, moved);
    setClients(ordered); // optimistic
    setDragId(null);
    setDragOverId(null);
    try {
      await axios.put(
        `${API}/admin/clients/reorder`,
        { ids: ordered.map((c) => c.id) },
        { headers }
      );
      onChange?.();
    } catch (err) {
      toast.error("Reorder failed");
      reload();
    }
  };

  return (
    <div className="space-y-4" data-testid="clients-manager">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
            Client projects · {clients.length} on showcase
          </div>
          <div className="mt-1 text-xs text-[#8b8b8f]">
            Drag rows to reorder · upload logo per client · supports JPG / PNG / WEBP / SVG (≤ 5 MB)
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowBulk((v) => !v)}
            data-testid="clients-toggle-bulk"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1c1c1e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
          >
            <Upload size={12} /> Bulk add
          </button>
          <button
            onClick={() => setEditForm(emptyForm())}
            data-testid="clients-new"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#e85a1c] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-[#c64812]"
          >
            <Plus size={12} /> New client
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search
          size={12}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8b8b8f]"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by company name…"
          data-testid="clients-search"
          className="w-full rounded-full border border-black/10 bg-white py-1.5 pl-8 pr-3 text-sm focus:border-[#e85a1c] focus:outline-none"
        />
      </div>

      {/* Bulk panel */}
      {showBulk && (
        <div className="rounded-2xl border border-black/10 bg-white p-4" data-testid="clients-bulk-panel">
          <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
            Bulk add — one name per line
          </div>
          <textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            rows={5}
            data-testid="clients-bulk-input"
            placeholder={"Acme Builders\nMy New Construction\nXYZ Corp"}
            className="mt-2 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              onClick={() => setShowBulk(false)}
              className="rounded-full border border-black/10 px-4 py-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.2em] text-[#4a4a4e] hover:border-[#1c1c1e] hover:text-[#1c1c1e]"
            >
              Cancel
            </button>
            <button
              onClick={submitBulk}
              data-testid="clients-bulk-submit"
              className="rounded-full bg-[#e85a1c] px-4 py-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.2em] text-white hover:bg-[#c64812]"
            >
              Add all
            </button>
          </div>
        </div>
      )}

      {/* Form modal (inline) */}
      {editForm && (
        <div className="rounded-2xl border border-[#e85a1c]/30 bg-white p-4 ring-2 ring-[#e85a1c]/10" data-testid="clients-form">
          <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
            {editForm.id ? "Edit client" : "New client"}
          </div>
          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
            <div className="md:col-span-3">
              <label className="font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#8b8b8f]">Company name *</label>
              <input
                value={editForm.company_name}
                onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                data-testid="clients-form-name"
                className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#8b8b8f]">Website (optional)</label>
              <input
                value={editForm.website_url}
                onChange={(e) => setEditForm({ ...editForm, website_url: e.target.value })}
                data-testid="clients-form-website"
                placeholder="https://"
                className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
              />
            </div>
            <div>
              <label className="font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#8b8b8f]">Projects (optional)</label>
              <input
                type="number"
                min={0}
                value={editForm.project_count}
                onChange={(e) => setEditForm({ ...editForm, project_count: e.target.value })}
                data-testid="clients-form-count"
                className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm focus:border-[#e85a1c] focus:outline-none"
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={() => setEditForm(null)}
              className="rounded-full border border-black/10 px-4 py-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.2em] text-[#4a4a4e] hover:border-[#1c1c1e] hover:text-[#1c1c1e]"
            >
              Cancel
            </button>
            <button
              onClick={saveForm}
              data-testid="clients-form-save"
              className="inline-flex items-center gap-1 rounded-full bg-[#e85a1c] px-4 py-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.2em] text-white hover:bg-[#c64812]"
            >
              <Check size={11} /> Save
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-2" data-testid="clients-list">
        {filtered.map((c) => {
          const isDragOver = dragOverId === c.id;
          return (
            <div
              key={c.id}
              draggable
              onDragStart={() => onDragStart(c.id)}
              onDragOver={(e) => onDragOver(e, c.id)}
              onDragEnd={onDragEnd}
              onDrop={onDragEnd}
              data-testid={`client-row-${c.id}`}
              className={`group flex items-center gap-3 rounded-xl border bg-white p-3 transition-all ${
                isDragOver ? "border-[#e85a1c]/40 ring-2 ring-[#e85a1c]/15" : "border-black/10"
              } ${dragId === c.id ? "opacity-50" : ""}`}
            >
              {/* Drag handle */}
              <button
                type="button"
                title="Drag to reorder"
                className="cursor-grab text-[#bababd] hover:text-[#1c1c1e]"
              >
                <GripVertical size={14} />
              </button>

              {/* Logo */}
              <div className="flex h-12 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-black/5 bg-[#f7f7f5]">
                {c.logo_url ? (
                  <img
                    src={c.logo_url}
                    alt={c.company_name}
                    loading="lazy"
                    className="max-h-10 max-w-full object-contain"
                  />
                ) : (
                  <Building2 size={16} className="text-[#bababd]" />
                )}
              </div>

              {/* Info */}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-[#1c1c1e]" title={c.company_name}>
                  {c.company_name}
                </div>
                <div className="flex items-center gap-2 font-mono-industrial text-[9px] uppercase tracking-[0.18em] text-[#8b8b8f]">
                  <span>#{(c.display_order ?? 0) + 1}</span>
                  {c.website_url && (
                    <a
                      href={c.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-[#e85a1c] hover:underline"
                    >
                      <ExternalLink size={9} /> website
                    </a>
                  )}
                  {c.project_count ? <span>{c.project_count} projects</span> : null}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1">
                <input
                  ref={(el) => {
                    logoInputRefs.current[c.id] = el;
                  }}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadLogo(c, f);
                    e.target.value = "";
                  }}
                  data-testid={`client-logo-input-${c.id}`}
                />
                <button
                  type="button"
                  onClick={() => logoInputRefs.current[c.id]?.click()}
                  data-testid={`client-logo-upload-${c.id}`}
                  title="Upload logo"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-white text-[#1c1c1e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                >
                  <ImagePlus size={11} />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setEditForm({
                      id: c.id,
                      company_name: c.company_name,
                      website_url: c.website_url || "",
                      project_count: c.project_count ?? "",
                    })
                  }
                  data-testid={`client-edit-${c.id}`}
                  title="Edit"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-white text-[#1c1c1e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                >
                  <Pencil size={11} />
                </button>
                <button
                  type="button"
                  onClick={() => removeClient(c)}
                  data-testid={`client-delete-${c.id}`}
                  title="Delete"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-white text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientsManager;
